import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { Heart, MessageCircle, Check, X } from "lucide-react";

const STATUSES = ["Sent", "Received", "Accepted", "Declined", "Pending"];

export default function MyMatches() {
  const [shortlist, setShortlist] = useState([]);
  const [sent, setSent] = useState([]);
  const [received, setReceived] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try {
      const [s, se, re] = await Promise.all([
        api.get("/interests/shortlist"),
        api.get("/interests/sent"),
        api.get("/interests/received"),
      ]);
      setShortlist(s.data.profiles);
      setSent(se.data.items);
      setReceived(re.data.items);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoaded(true); }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try { await api.patch(`/interests/${id}/status`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const allEmpty = loaded && received.length === 0 && sent.length === 0 && shortlist.length === 0;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-slate-900">My Matches</h1>
      <p className="text-slate-600 mt-1">Shortlists, interests, and conversations — all in one place.</p>

      {allEmpty && (
        <Card className="mt-8 p-10 text-center border-2 border-[#D4AF37] bg-gradient-to-br from-[#FAF6F0] to-white plan-card-featured" data-testid="matches-empty-cta">
          <Heart className="w-12 h-12 mx-auto text-[#6B1D2F]" />
          <h2 className="mt-4 font-serif text-2xl text-slate-900">Let's find your match</h2>
          <p className="text-slate-600 mt-2 max-w-lg mx-auto">
            You haven't shortlisted anyone or exchanged interests yet. Start by searching for profiles that fit your community, profession and preferences.
          </p>
          <Button
            data-testid="matches-empty-search-btn"
            onClick={() => nav("/search")}
            className="mt-6 bg-[#6B1D2F] hover:bg-[#541624] text-white rounded-full px-8 py-6"
          >
            Search profiles
          </Button>
        </Card>
      )}

      <Tabs defaultValue="received" className="mt-8">
        <TabsList className="bg-[#FAF6F0] border border-[#EAE3D2]">
          <TabsTrigger value="received" data-testid="tab-received">Received ({received.length})</TabsTrigger>
          <TabsTrigger value="sent" data-testid="tab-sent">Sent ({sent.length})</TabsTrigger>
          <TabsTrigger value="shortlist" data-testid="tab-shortlist">Shortlist ({shortlist.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="received" className="mt-6">
          <Grid empty="No interests received yet.">
            {received.map(({ interest, profile }) => (
              <ItemCard key={interest.id} profile={profile}>
                <Badge className="bg-slate-100 text-slate-800 mb-2">{interest.status}</Badge>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" data-testid={`accept-${interest.id}`} onClick={() => updateStatus(interest.id, "Accepted")} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full flex-1"><Check className="w-4 h-4 mr-1" /> Accept</Button>
                  <Button size="sm" data-testid={`decline-${interest.id}`} variant="outline" onClick={() => updateStatus(interest.id, "Declined")} className="rounded-full flex-1"><X className="w-4 h-4 mr-1" /> Decline</Button>
                </div>
              </ItemCard>
            ))}
          </Grid>
        </TabsContent>

        <TabsContent value="sent" className="mt-6">
          <Grid empty="You haven't expressed interest in anyone yet.">
            {sent.map(({ interest, profile }) => (
              <ItemCard key={interest.id} profile={profile}>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs text-slate-500">Status:</span>
                  <Select value={interest.status} onValueChange={(v) => updateStatus(interest.id, v)}>
                    <SelectTrigger className="h-8 w-32" data-testid={`sent-status-${interest.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {interest.status === "Accepted" && (
                  <Button size="sm" onClick={() => nav(`/chat/${profile.id}`)} data-testid={`chat-${profile.id}`} className="mt-2 w-full bg-[#6B1D2F] hover:bg-[#541624] text-white rounded-full">
                    <MessageCircle className="w-4 h-4 mr-1" /> Open chat
                  </Button>
                )}
              </ItemCard>
            ))}
          </Grid>
        </TabsContent>

        <TabsContent value="shortlist" className="mt-6">
          <Grid empty="No profiles shortlisted yet.">
            {shortlist.map((p) => <ItemCard key={p.id} profile={p} />)}
          </Grid>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Grid({ children, empty }) {
  const arr = React.Children.toArray(children);
  if (arr.length === 0) return <Card className="p-10 text-center border-[#EAE3D2] bg-white text-slate-600">{empty}</Card>;
  return <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">{children}</div>;
}

function ItemCard({ profile, children }) {
  return (
    <Card className="overflow-hidden border-[#EAE3D2] bg-white">
      <Link to={`/profile/${profile.id}`} data-testid={`match-profile-${profile.id}`}>
        <div className="h-40 bg-[#F3EFE6]">
          {profile.profile_photo_path ? (
            <img src={fileUrl(profile.profile_photo_path)} alt={profile.full_name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#6B1D2F]/10 to-[#D4AF37]/10">
              <span className="font-serif text-4xl text-[#6B1D2F]/50">{profile.full_name?.[0]}</span>
            </div>
          )}
        </div>
      </Link>
      <div className="p-4">
        <h3 className="font-serif text-lg text-slate-900">{profile.full_name}, {profile.age}</h3>
        <p className="text-xs text-[#B8860B] font-semibold uppercase tracking-wider">{profile.caste} · {profile.visa_type}</p>
        {children}
      </div>
    </Card>
  );
}
